import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldAlert, Clock, ShieldX, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMeta = {
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700 border-green-200', icon: ShieldCheck },
  pending: { label: 'Under Review', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldX },
  not_submitted: { label: 'Not Submitted', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: ShieldAlert },
};

const KYCCard = ({ onChange }) => {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pan, setPan] = useState('');
  const [aadhaar, setAadhaar] = useState('');

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/kyc/status`);
      setKyc(res.data);
    } catch (e) {
      console.error('Failed to fetch KYC status', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/kyc/submit`, {
        pan_number: pan.trim().toUpperCase(),
        aadhaar_number: aadhaar.replace(/\s+/g, ''),
      });
      toast.success('KYC submitted! Awaiting admin verification.');
      setPan(''); setAadhaar('');
      await fetchStatus();
      if (onChange) onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !kyc) return null;

  const status = kyc.kyc_status || 'not_submitted';
  const meta = statusMeta[status] || statusMeta.not_submitted;
  const StatusIcon = meta.icon;
  const showForm = status === 'not_submitted' || status === 'rejected';

  return (
    <Card className="mb-8 border-l-4 border-l-secondary" data-testid="kyc-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-secondary" />
            <div>
              <CardTitle className="text-xl font-heading">Identity Verification (KYC)</CardTitle>
              <CardDescription>PAN &amp; Aadhaar verification is required before you can bid on tasks.</CardDescription>
            </div>
          </div>
          <Badge className={`gap-1 border ${meta.color}`} data-testid="kyc-status-badge">
            <StatusIcon className="w-3.5 h-3.5" /> {meta.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {kyc.hitch_id && (
          <div className="mb-4 text-sm" data-testid="kyc-hitch-id">
            <span className="text-muted-foreground">Your Hitch ID: </span>
            <span className="font-mono font-semibold text-primary bg-slate-100 px-2 py-1 rounded">{kyc.hitch_id}</span>
          </div>
        )}

        {status === 'verified' && (
          <p className="text-sm text-green-700" data-testid="kyc-verified-msg">
            Your identity is verified. You are eligible to bid on tasks.
          </p>
        )}

        {status === 'pending' && (
          <p className="text-sm text-amber-700" data-testid="kyc-pending-msg">
            Your documents (PAN: {kyc.pan_number}) are under review. You'll be notified once verified.
          </p>
        )}

        {status === 'rejected' && kyc.kyc_rejection_reason && (
          <p className="text-sm text-red-600 mb-4" data-testid="kyc-rejected-msg">
            Rejected: {kyc.kyc_rejection_reason}. Please re-submit with correct details.
          </p>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2" data-testid="kyc-form">
            <div>
              <Label htmlFor="pan">PAN Number</Label>
              <Input
                id="pan" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                placeholder="ABCDE1234F" maxLength={10} required
                data-testid="kyc-pan-input" className="uppercase font-mono"
              />
            </div>
            <div>
              <Label htmlFor="aadhaar">Aadhaar Number</Label>
              <Input
                id="aadhaar" value={aadhaar} onChange={(e) => setAadhaar(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="123456789012" maxLength={12} required inputMode="numeric"
                data-testid="kyc-aadhaar-input" className="font-mono"
              />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={submitting} className="bg-secondary text-secondary-foreground" data-testid="kyc-submit-button">
                {submitting ? 'Submitting...' : 'Submit for Verification'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default KYCCard;
